import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { PayloadLexicalReactRendererContent } from '@atelier-disko/payload-lexical-react-renderer';
import { Button, Form, Input, message, Rate } from 'antd';
import cn from 'classnames';

import { type Form as FormType, RichTextRenderer } from '@/shared/cms/shared';
import { useAntNotification } from '@/shared/lib/hooks';
import { logError } from '@/shared/lib/logging';
import { generateRecaptchaToken, useRecaptcha } from '@/shared/lib/recaptcha';
import { GoogleRecaptchaNotice } from '@/shared/ui/google-recaptcha-notice';
import { InputSelect, InputText } from '@/shared/ui/input';

import { usePostFormMutation } from '../../api/form.api';

import cls from './form-renderer.module.css';

type FormFieldBase = {
    name: string;
    label?: string | null;
    width?: number | null;
    required?: boolean | undefined;
    id?: string | null;
    blockName?: string | null;
};

type TextFieldType = FormFieldBase & {
    blockType: 'text';
    defaultValue?: string | null;
    placeholder?: string | null;
};

type TextAreaFieldType = FormFieldBase & {
    blockType: 'textarea';
    defaultValue?: string | null;
    placeholder?: string | null;
};

type SelectFieldType = FormFieldBase & {
    blockType: 'select';
    defaultValue?: string | null;
    placeholder?: string | null;
    options?:
        | {
              label: string;
              value: string;
              id?: string | null;
          }[]
        | null;
};

type StarFieldType = FormFieldBase & {
    blockType: 'number';
    defaultValue?: number | null;
};

// Union type for all supported field types
type FormFieldType =
    | TextFieldType
    | TextAreaFieldType
    | SelectFieldType
    | StarFieldType;

// Type for form submission values
type FormValues = Record<string, string | string[]>;

type Props = {
    form: FormType;
    userEmail?: string | undefined;
    onSubmitSuccess?: () => void;
    onSubmitError?: () => void;
    className?: string;
};

export function FormRenderer(props: Props) {
    const { className, form, userEmail, onSubmitSuccess, onSubmitError } =
        props;

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [antForm] = Form.useForm();
    const { t } = useTranslation('translation');
    const [postFormMutation] = usePostFormMutation();

    // Get notification functions from context
    const { showError, showSuccess } = useAntNotification();

    // Get reCAPTCHA status
    const { isLoaded: isRecaptchaLoaded, error: recaptchaError } =
        useRecaptcha();

    const handleSubmit = useCallback(
        async (values: FormValues) => {
            try {
                setIsSubmitting(true);

                // Check reCAPTCHA status
                if (recaptchaError) {
                    throw new Error(`reCAPTCHA error: ${recaptchaError}`);
                }

                if (!isRecaptchaLoaded) {
                    throw new Error(
                        'reCAPTCHA not loaded yet. Please try again.',
                    );
                }

                // Generate reCAPTCHA token
                const recaptchaToken =
                    await generateRecaptchaToken('form_submission');

                if (!recaptchaToken) {
                    throw new Error(
                        'Failed to generate reCAPTCHA token. Please try again.',
                    );
                }

                const submissionData = Object.entries(values).map(
                    ([name, value]) => ({
                        field: name,
                        value: Array.isArray(value) ? value.join(',') : value,
                    }),
                );

                await postFormMutation({
                    form: form.id,
                    submissionData,
                    recaptchaToken,
                });

                if (onSubmitSuccess) onSubmitSuccess();

                antForm.resetFields();

                // Show confirmation message if available
                if (
                    form.confirmationType === 'message' &&
                    form.confirmationMessage
                ) {
                    // The message is already shown by the confirmation section
                    showSuccess?.({
                        message: (
                            <RichTextRenderer
                                content={
                                    form.confirmationMessage as PayloadLexicalReactRendererContent
                                }
                            />
                        ),
                    });
                } else {
                    message.success('Form submitted successfully');
                }
            } catch (err) {
                logError('Error submitting form:', err);
                showError?.({ message: t('form.confirmationError') });

                if (onSubmitError) onSubmitError();
            } finally {
                setIsSubmitting(false);
            }
        },
        [
            antForm,
            form.confirmationMessage,
            form.confirmationType,
            form.id,
            isRecaptchaLoaded,
            onSubmitError,
            onSubmitSuccess,
            postFormMutation,
            recaptchaError,
            showError,
            showSuccess,
            t,
        ],
    );

    const renderField = useCallback(
        (field: FormFieldType) => {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            const { blockType, name, label, required, width } = field;

            const formItemProps = {
                key: field.id || name,
                label,
                name,
                required,
                rules: required
                    ? [{ required: true, message: t('form.required') }]
                    : [],
                style: width ? { width: `${width}%` } : undefined,
            };

            switch (blockType) {
                case 'text':
                    return (
                        <Form.Item
                            key={formItemProps.key}
                            label={formItemProps.label}
                            name={formItemProps.name}
                            rules={formItemProps.rules}
                        >
                            <InputText
                                isBlock
                                placeholder={field.placeholder || undefined}
                            />
                        </Form.Item>
                    );
                case 'textarea':
                    return (
                        <Form.Item
                            key={formItemProps.key}
                            label={formItemProps.label}
                            name={formItemProps.name}
                            rules={formItemProps.rules}
                        >
                            <Input.TextArea
                                placeholder={field.placeholder || undefined}
                                rows={4}
                            />
                        </Form.Item>
                    );
                case 'select':
                    return (
                        <Form.Item
                            key={formItemProps.key}
                            label={formItemProps.label}
                            name={formItemProps.name}
                            rules={formItemProps.rules}
                        >
                            <InputSelect
                                isBlock
                                options={
                                    field.options?.map((opt) => ({
                                        label: opt.label,
                                        value: opt.value,
                                    })) || []
                                }
                                placeholder={field.placeholder || undefined}
                            />
                        </Form.Item>
                    );
                case 'number':
                    // Special handling for star rating fields
                    if (name === 'stars') {
                        return (
                            <Form.Item
                                key={formItemProps.key}
                                label={formItemProps.label}
                                name={formItemProps.name}
                                rules={formItemProps.rules}
                            >
                                <Rate
                                    allowHalf={false}
                                    count={5}
                                    defaultValue={field.defaultValue || 0}
                                />
                            </Form.Item>
                        );
                    }

                    return null;
                default:
                    return null;
            }
        },
        [t],
    );

    return (
        <div className={cn(cls.root, className)}>
            <Form
                autoComplete="off"
                className={cls.form}
                form={antForm}
                layout="vertical"
                onFinish={handleSubmit}
            >
                {/* Hidden email field - automatically added to all forms */}
                <Form.Item
                    initialValue={userEmail}
                    name="email"
                    style={{ display: 'none' }}
                >
                    <Input type="hidden" />
                </Form.Item>

                {form.fields?.map((field) => {
                    // Only render fields of supported types
                    if (
                        field.blockType === 'text' ||
                        field.blockType === 'select' ||
                        field.blockType === 'textarea' ||
                        (field.blockType === 'number' && field.name === 'stars')
                    ) {
                        return renderField(field as FormFieldType);
                    }

                    return null;
                })}

                <GoogleRecaptchaNotice />

                <Form.Item className={cls.submitButton}>
                    <Button
                        disabled={!isRecaptchaLoaded || !!recaptchaError}
                        htmlType="submit"
                        loading={isSubmitting}
                        type="primary"
                    >
                        {form.submitButtonLabel || 'Submit'}
                    </Button>
                    {recaptchaError && (
                        <div
                            style={{
                                color: 'red',
                                marginTop: '8px',
                                fontSize: '12px',
                            }}
                        >
                            {t('form.recaptchaError', {
                                defaultValue:
                                    'reCAPTCHA error. Please refresh the page.',
                            })}
                        </div>
                    )}
                    {!isRecaptchaLoaded && !recaptchaError && (
                        <div
                            style={{
                                color: '#666',
                                marginTop: '8px',
                                fontSize: '12px',
                            }}
                        >
                            {t('form.recaptchaLoading', {
                                defaultValue:
                                    'Loading security verification...',
                            })}
                        </div>
                    )}
                </Form.Item>
            </Form>
        </div>
    );
}

FormRenderer.displayName = 'FormRenderer';
